import { datadogRum } from '@datadog/browser-rum';
import { nextjsPlugin } from '@datadog/browser-rum-nextjs';

export { onRouterTransitionStart } from '@datadog/browser-rum-nextjs';

/**
 * Deployment variables can be populated with BLANK placeholders before real
 * Datadog RUM credentials are available. Treat them as missing so Browser RUM
 * is not initialised with unusable public values.
 */
const isConfiguredValue = (value: string | undefined): value is string =>
  Boolean(value && value.trim() && value.trim().toUpperCase() !== 'BLANK');

/**
 * Browser RUM expects sampling rates as percentages. Blank/placeholder values
 * fall back to the product defaults, while configured values are clamped to
 * Datadog's valid 0-100 range.
 */
const parseSampleRate = (value: string | undefined, fallback: number) => {
  if (!isConfiguredValue(value)) {
    return fallback;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.min(100, Math.max(0, parsedValue));
};

const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
const datadogEnabled =
  process.env.NEXT_PUBLIC_DATADOG_ENABLED?.toLowerCase() === 'true';
const datadogModeAllowsMonitoring =
  process.env.NEXT_PUBLIC_AEM_MODE !== 'preview';
const datadogRumCredentials =
  isConfiguredValue(applicationId) && isConfiguredValue(clientToken)
    ? { applicationId, clientToken }
    : null;
const applicationIdConfigured = Boolean(datadogRumCredentials?.applicationId);
const clientTokenConfigured = Boolean(datadogRumCredentials?.clientToken);
const shouldInitialise =
  datadogEnabled &&
  datadogModeAllowsMonitoring &&
  applicationIdConfigured &&
  clientTokenConfigured;

if (shouldInitialise && datadogRumCredentials) {
  /**
   * Keep the browser integration close to Datadog's generated Next.js setup:
   * Browser RUM is initialised once in instrumentation-client, while the
   * Next.js RUM plugin lets DatadogPagesRouter report normalised Pages Router
   * view names.
   */
  datadogRum.init({
    applicationId: datadogRumCredentials.applicationId,
    clientToken: datadogRumCredentials.clientToken,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com',
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'browse-nextjs',
    env: process.env.NEXT_PUBLIC_ENVIRONMENT_NAME || 'local',
    version: process.env.NEXT_PUBLIC_DATADOG_VERSION,
    sessionSampleRate: parseSampleRate(
      process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
      100,
    ),
    sessionReplaySampleRate: parseSampleRate(
      process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
      20,
    ),
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [nextjsPlugin()],
  });
}
